import { useCallback, useEffect, useRef, useState } from "react";
import useSecureCable from "../utils/useSecureCable";
import { useDispatch, useSelector } from "react-redux";
import { commentActionConstants, userConstants } from "../constants";
import { alertActions, commentActions } from "../actions";
import { createCancelToken } from "../utils/Axios";

function converScopeName(activeTab) {
    switch(activeTab) {
        case 'internal_notes':
            return 'team';
        case 'discussion':
            return 'guest';
        default:
            return null;
    }
}

function useCommentActionCable({
    card_id,
    workspace_id,
    show_activities,
    per_page
}) {
    const dispatch = useDispatch();
    const secureCable = useSecureCable({refreshUrl: '/api/v1/action_cable/refresh_workspace_token', workspace_id});

    // Redux 
    const { getCommonData } = useSelector(state => state.dashboard);
    const { activeTab, comments } = useSelector(state => state.comment);
    const user_id = getCommonData?.current_user?.id;
    const scope = converScopeName(activeTab);

    // State
    const [connected, setConnected] = useState(false);
    
    // Refs 
    const activeSubRef = useRef(null);
    const subscriptionRef = useRef(null);
    const channelName = `CardChatChannel`;

    const handleReceivedMessage = useCallback((result) => {
        const { data, type } = result.data;
        const is_user = data?.user?.id === user_id;


        console.log("📨 Received message on CardChatChannel:", result);
        switch(type) {
            case 'create':
                // Handle new comment received
                dispatch({ type: commentActionConstants.ADD_COMMENT, payload: { ...data, is_user } });

                break;
            case 'update':
                // Handle comment update received
                dispatch({ type: commentActionConstants.UPDATE_COMMENT, payload: { ...data, is_user } });

                break;
            case 'destroy':
                // Handle comment deletion received
                dispatch({ type: commentActionConstants.UPDATE_COMMENT, payload: { ...data, is_user } });
                break;
            default:
                console.log('Unknown message type:', type);
        }
    }, [user_id]);

    const getComments = () => {
        const page = 1;
        const load = false;
        if(activeTab === "" || activeTab === null) return;

        const onSuccess = (res) => {
            console.log("✅ Fetched comments via Action Cable:", res.data);
            dispatch({ type: commentActionConstants.ADD_COMMENTS, payload: res.data.data });
            dispatch({ type: commentActionConstants.SET_HAS_NEXT_PAGE, payload: res.data.has_next_page });
            dispatch({ type: commentActionConstants.SET_CAN_ACCESS_COMMENT, payload: res.data.can_access_comment });

            dispatch({ type: userConstants.COMMENT_LOADING_FINISHED });
            dispatch({ type: userConstants.UPDATE_COMMENT_LOADING_FINISHED });
        }
        const onError = (response) => {
            dispatch({ type: userConstants.COMMENT_FAILURE, payload: response.data.message});
            dispatch(alertActions.error(response.data.message));
        }

        const params = {
            workspace_id,
            content_group_id: card_id,
            params: {
                chat_status: activeTab,
                show_activities: show_activities,
                page,
                per_page: per_page
            },
            cancel_token: createCancelToken().token,
            onSuccess,
            onError
        }
        console.log('🔍 Fetching comments via Action Cable with params:', params)
        dispatch(commentActions.getRevisedCommentControlled(params));
    }


    useEffect(() => {
        if (!card_id || !scope || !user_id || !secureCable) return;

        const subscriptionKey = `${channelName}-${workspace_id}-${scope}-${card_id}-${user_id}`;
        if (activeSubRef.current === subscriptionKey) {
            console.log(`🔄 Subscription already active for ${subscriptionKey}`);
            return;
        }

        activeSubRef.current = subscriptionKey;

        const subscribe = () => {
            const channel = { channel: channelName, card_id, scope };

            if (subscriptionRef.current) secureCable.subscriptions.remove(subscriptionRef.current);

            subscriptionRef.current = secureCable.subscriptions.create(channel, {
            connected: () => {
                // Also fires on ActionCable's replay after a reconnect.
                console.log(`✅ Connected to ${channelName}:`, channel);
                setConnected(true);
                getComments(); // fetch initial comments
            },
            disconnected: () => {
                console.warn(`⚠️ Disconnected from ${channelName}`);
                setConnected(false);
            },
            rejected: () => {
                setConnected(false);
                console.error(`❌ Subscription rejected by ${channelName}`);
            },
            received: handleReceivedMessage,
            });

            console.log(`🧠 Subscribed to ${channelName}:`, channel);
            return true;
        };

        subscribe();

        return () => {
            if (subscriptionRef.current) secureCable.subscriptions.remove(subscriptionRef.current);
            subscriptionRef.current = null;
            console.log(`🧹 Unsubscribed from ${channelName}`);
            activeSubRef.current = null;
        };
    }, [card_id, scope, user_id, secureCable]);

    return { connected  };
}

export default useCommentActionCable;